<template>
  <div class="pagination flex items-center justify-center p-4 gap-1" v-if="pagination?.total > 0">
    <button @click="prevPage" :disabled="pagination.current === 1">
      <img class="size-4" :src="imagePath.leftArrowIcon" alt="left_arrow" />
    </button>
    <button v-for="page in pageNumbers" :key="page" @click="changePage(page)" :class="{ active: page === pagination.current }">
      {{ page }}
    </button>
    <button @click="nextPage" :disabled="pagination.current === totalPages">
      <img class="size-4" :src="imagePath.rightArrowIcon" alt="right_arrow" />
    </button>

    <CustomDropdown v-if="allowChangePageSize" v-model="selectedPerPage" maxHeight="200px" gap="10px" position="top">
      <template #trigger>
        <div class="flex flex-row gap-1 items-center text-sm font-normal cursor-pointer ml-2">
          {{ selectedPerPage }} per page

          <img class="size-5" :src="imagePath.arrowDownTooltip" alt="bottom_arrow" />
        </div>
      </template>

      <template #default>
        <div v-for="item in perPageOptions" :key="item" class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg" @click="updatePerPage(item)">
          <span class="proxima_nova text-sm">{{ item }} per page</span>
        </div>
      </template>
    </CustomDropdown>
  </div>
</template>

<script setup>
import CustomDropdown from '@/components/custom/CustomDropdown.vue';
import imagePath from '@/javascript/imagePath';
import { computed, ref } from 'vue';

const props = defineProps({
  pagination: Object,
  allowChangePageSize: { type: Boolean, required: false, default: true },
});

const emit = defineEmits(['update:pagination']);

const selectedPerPage = ref(props.pagination?.pageSize || 20);
const perPageOptions = [20, 50, 100];

const updatePerPage = (item) => {
  selectedPerPage.value = item;
  emit('update:pagination', {
    ...props.pagination,
    pageSize: item,
    selection: true,
  });
};

const totalPages = computed(() => Math.ceil(props.pagination.total / props.pagination.pageSize));

const nextPage = () => {
  if (props.pagination.current < totalPages.value) {
    emit('update:pagination', {
      ...props.pagination,
      current: props.pagination.current + 1,
      selection: false,
    });
  }
};

const prevPage = () => {
  if (props.pagination.current > 1) {
    emit('update:pagination', {
      ...props.pagination,
      current: props.pagination.current - 1,
      selection: false,
    });
  }
};

const changePage = (page) => {
  emit('update:pagination', {
    ...props.pagination,
    current: page,
    selection: false,
  });
};

const pageNumbers = computed(() => {
  const total = totalPages.value;
  const current = props.pagination.current;
  const range = [];
  if (total <= 5) {
    for (let i = 1; i <= total; i++) {
      range.push(i);
    }
  } else {
    if (current <= 3) {
      range.push(1, 2, 3, 4, 5);
    } else if (current >= total - 2) {
      range.push(total - 4, total - 3, total - 2, total - 1, total);
    } else {
      range.push(current - 2, current - 1, current, current + 1, current + 2);
    }
  }
  return range;
});
</script>

<style>
.pagination button {
  display: flex;
  height: 32px;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  color: var(--foreground-rgb);
  font-family: 'Proxima Nova', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.pagination button:disabled {
  cursor: not-allowed;
}

.pagination button:hover:not(:disabled) {
  cursor: pointer;
}

.pagination button.active {
  border-radius: 8px;
  border: 1px solid var(--white-opacity-12);
  background: var(--white-opacity-8);
}

.pagination span {
  margin: 0 8px;
}
</style>
